<template>
  <div>
    <AspectRatio :height="aspectHeight" :width="aspectWidth" class="z-0 group video-player-container">

      <video
        v-if="isBlobSource"
        :src="src"
        class="h-full w-full rounded-lg bg-black object-contain"
        controls
        playsinline
      />

      <div v-else-if="errorCode" class="flex min-h-full w-full items-center justify-center rounded-lg bg-black text-white">
        <p>
          <ExclamationTriangleIcon class="mr-2 inline-block h-4 w-4 text-amber-500"/>
          <span v-if="errorCode === ERROR_CODE_VIDEO_NOT_FOUND" class="font-medium">404 • Video Not Found</span>
          <span v-else-if="errorCode === ERROR_CODE_VIDEO_NOT_PLAYABLE" class="font-medium">Video Not Playable</span>
        </p>
      </div>

      <div v-else-if="!src" class="flex min-h-full w-full items-center justify-center rounded-lg bg-black">
        <AnimationSpinningLoader diameter="5rem" border-size="0.375rem"/>
      </div>

      <media-player
        v-else
        :key="src"
        :autoplay="autoplay"
        class="h-full w-full cursor-pointer overflow-hidden bg-black font-sans text-white ring-media-focus data-[focus]:ring-4"
        :style="mediaPlayerStyles"
        :title="title"
        loop="true"
        crossorigin="anonymous"
        controlsDelay="1000"
        view-type="video"
        load="visible"
        :src="src"
        ref="$player">

        <media-provider>
          <media-poster
            v-if="poster"
            class="absolute inset-0 block h-full w-full opacity-0 transition-opacity [&>img]:h-full [&>img]:w-full [&>img]:object-contain data-[visible]:opacity-100"
            :src="poster"
            :alt="posterAlt"
          />

          <track
            v-if="englishSubtitlesVtt"
            :src="englishSubtitlesVtt"
            label="English"
            srclang="en-US"
            kind="subtitles"
            default/>

          <track
            v-if="spanishSubtitlesVtt"
            :src="spanishSubtitlesVtt"
            label="Spanish"
            srclang="es"
            kind="subtitles"/>

          <track
            v-if="englishChaptersVtt"
            :src="englishChaptersVtt"
            srclang="en-US"
            kind="chapters"
            default/>

        </media-provider>

        <VideoPlayerLayout :thumbnails="thumbnails" :is-small="isSmallPlayer" @play="play" @pause="pause"
                           :has-chapters="!!englishChaptersVtt"/>

      </media-player>

    </AspectRatio>
  </div>
</template>

<script setup>
import "vidstack/player/styles/base.css";
import "vidstack/player";
import "vidstack/player/ui";
import "vidstack/icons";
import {computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch} from "vue";
import {ExclamationTriangleIcon} from "@heroicons/vue/24/solid/index.js";
import AspectRatio from "../components/AspectRatio.vue";
import AnimationSpinningLoader from "./AnimationSpinningLoader.vue";
import VideoPlayerLayout from "./VideoPlayerLayout.vue";

const emits = defineEmits(["update:currentTime", "update:duration", "update:controlsVisible", "update:ended", "update:width"]);
const props = defineProps({
  src: String,
  title: String,
  aspectHeight: Number,
  aspectWidth: Number,
  poster: String,
  posterAlt: String,
  englishChaptersVtt: String,
  englishSubtitlesVtt: String,
  spanishSubtitlesVtt: String,
  thumbnails: String,
  initialMute: Boolean,
  autoplay: Boolean,
  videoBorderRadius: {
    type: String,
    default: "0.375rem"
  }
});

const $player = ref();
const errorCode = ref(null);
const playerWidth = ref(0);

const mediaPlayerStyles = computed(() => `--media-slider-track-bg:rgba(255,255,255,0.2);--media-slider-track-progress-bg:rgba(255,255,255,0.4);--media-brand:#dc2626;--media-slider-track-fill-bg:#dc2626;--video-border-radius:${props.videoBorderRadius};`);

const play = () => $player.value.play();
const pause = () => $player.value.pause();

const PLAY_BUTTON_BREAKPOINT = 678;
const ERROR_CODE_VIDEO_NOT_FOUND = 4;
const ERROR_CODE_VIDEO_NOT_PLAYABLE = 5;

const handleUpdateWidth = (width) => {
  emits("update:width", width);
  playerWidth.value = width;
};

const isSmallPlayer = computed(() => playerWidth.value < PLAY_BUTTON_BREAKPOINT);
const isBlobSource = computed(() => typeof props.src === "string" && props.src.startsWith("blob:"));

const handleError = (error) => {
  if (!error || !error.code || !(error.code === ERROR_CODE_VIDEO_NOT_FOUND || error.code === ERROR_CODE_VIDEO_NOT_PLAYABLE)) return;
  console.error(error);
  errorCode.value = error.code;
};

const initializePlayer = async () => {
  errorCode.value = null;

  await nextTick();

  $player.value.muted = !!props.initialMute;

  $player.value.subscribe(({currentTime, duration, ended, width, controlsVisible, error}) => {
    emits("update:currentTime", currentTime);
    emits("update:duration", duration);
    emits("update:ended", ended);
    emits("update:controlsVisible", controlsVisible);
    handleUpdateWidth(width);
    handleError(error);
  });

  if (props.autoplay) $player.value.play();
};

provide("title", props.title);

watch(() => props.src, (src) => src && !isBlobSource.value && initializePlayer());
onMounted(() => props.src && !isBlobSource.value && initializePlayer());
onBeforeUnmount(() => $player.value?.destroy());
</script>

<style>
media-live-button {
  display: none !important;
}

[data-media-provider] video {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
}
</style>
